import {
  Theme,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  TextField,
  styled,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Courtage } from '../../api/useFunds';

export const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: theme.spacing(11),
  justifyContent: 'space-between',
  [theme.breakpoints.only('xs')]: {
    display: 'block',
  },
}));

export const HeadingContainer = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
});

export const FundNotAvailableWarning = styled(Typography)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
  marginTop: `-${theme.spacing(4)}`,
}));

export const Heading = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

export const Centered = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  flexWrap: 'nowrap',
  justifyContent: 'flex-start',
  alignItems: 'center',
  textAlign: 'center',
}));

export const CenteredMobileOnly = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.only('xs')]: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
  },
}));

export const CenteredDesktopOnly = styled('div')(({ theme }) => ({
  textAlign: 'center',
  [theme.breakpoints.only('xs')]: {
    display: 'none',
  },
}));

export const SpacerRightXs = styled('div')(({ theme }) => ({
  [theme.breakpoints.only('xs')]: {
    marginRight: theme.spacing(1),
  },
}));

export const LabelText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '22px',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
  textAlign: 'center',

  [theme.breakpoints.only('sm')]: {
    fontSize: '11px',
    marginBottom: 0,
  },
}));

export const SubLabelText = styled(Typography)(() => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
}));

export const YesIcon = styled(CheckCircleIcon)(({ theme }: { theme: Theme }) => {
  return {
    color: theme.palette.icon.success,
  };
});

export const NoIcon = styled(RemoveIcon)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.error.main,
}));

export const WarnIcon = styled(ErrorIcon)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.warning.main,
}));

export const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => {
  return {
    [`& .${tooltipClasses.arrow}`]: {
      color: 'white',
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      boxShadow: '0px 2px 4px -1px rgba(30, 39, 50, 0.16)',
      boderRadius: '6px',
    },
  };
});

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& input': {
    padding: theme.spacing(3, 4),
  },
}));

interface VlIconProps {
  courtage?: Courtage;
  testId: string;
}

export const VlIcon = ({ courtage, testId }: VlIconProps) => {
  const { t } = useTranslation();
  return (
    <>
      <LabelText>{t('cashbackCalculator.detailed.fundInput.vl')}</LabelText>
      {courtage?.hasVl ? (
        <YesIcon data-testid={`has-${testId}`} />
      ) : (
        <Typography color="common.N400" fontSize="18px" data-testid={`no-${testId}`}>
          -
        </Typography>
      )}
    </>
  );
};

export const Commission = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  lineHeight: '28px',
  fontWeight: 600,
  marginBottom: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    marginBottom: theme.spacing(0.5),
  },
}));
